import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BookOpenIcon, ClockIcon, SearchIcon, XIcon } from 'lucide-react';
import { programDomains, programTemplates, programsIndexSlug } from '../data/programTemplates';
import { usePageMeta } from '../hooks/usePageMeta';
import { LegalFooter } from '../components/LegalFooter';
const ANY = '';

/**
 * Searchable index of every /program-information/ page.
 *
 * Programs are filterable by credential and workforce domain, and searchable
 * across name, hero copy, features, and career roles — so someone searching
 * "phlebotomy" or "ICD-10" lands on the right program even when that term is
 * not in the title.
 */
export function ProgramInformationIndexPage() {
  usePageMeta('Online Program Information | Online College Guide', 'Search accredited online degree and diploma programs by credential and field. Compare length, credits, and career outcomes for each program.');
  const [query, setQuery] = useState('');
  const [credential, setCredential] = useState(ANY);
  const [domain, setDomain] = useState(ANY);
  const credentials = useMemo(() => Array.from(new Set(programTemplates.map(p => p.credential))).sort(), []);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return programTemplates.filter(program => {
      if (credential && program.credential !== credential) return false;
      if (domain && program.domainSlug !== domain) return false;
      if (!q) return true;
      return [program.name, program.credential, program.heroSubhead, program.metaDescription, ...program.features, ...program.careerRoles, ...(program.concentrations?.map(c => c.name) ?? [])].join(' ').toLowerCase().includes(q);
    });
  }, [query, credential, domain]);
  const isFiltered = Boolean(query.trim() || credential || domain);
  const clearFilters = () => {
    setQuery('');
    setCredential(ANY);
    setDomain(ANY);
  };
  const domainName = (slug: string) => programDomains.find(d => d.slug === slug)?.name ?? '';
  const selectClass = 'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors';
  return <main className="bg-white" data-id="element-2471" data-mp="17rpk8fh2gd8e">
      {/* Hero */}
      <header className="hero-bg" data-id="element-2472" data-mp="1cnxv3go8dk9r">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-2473" data-mp="1cr25www7be9e">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-2474" data-mp="1gozwa31pk6ed">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-2475" data-mp="1lp5s7vf0gf29">
              <li data-id="element-2476" data-mp="1qoicq9ieta1o">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-2477" data-mp="1qp0ibwqyp0m9">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2478" data-mp="3niva0j7vq74i">/</li>
              <li className="text-white/90" data-id="element-2479" data-mp="3sfve4ol64zsz">Program Information</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl" data-id="element-2480" data-mp="3kwchhfj0581x">
            Program Information
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-2481" data-mp="3g4kzhtdwwane">
            Detailed breakdowns of what each program covers, how long it takes,
            and where it leads — so you can compare before you commit.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-id="element-2482" data-mp="7p17sysw30ag">
        {/* Search + filters */}
        <div className="mb-10" data-id="element-2483" data-mp="2satafwqgcr5">
          <label htmlFor="program-search" className="sr-only" data-id="element-2484" data-mp="2p5470o3tavf">
            Search programs
          </label>
          <div className="relative max-w-xl mb-4" data-id="element-2485" data-mp="3oy7qz7b36rqs">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" data-id="element-2486" data-mp="3oxef5rx9tc1s" />
            <input id="program-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search programs — try “coding”, “patient care”, or “claims”" className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-2487" data-mp="2direy2aeanyj" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-xl" data-id="element-2488" data-mp="oybx5omvrux0">
            <div data-id="element-2489" data-mp="oyv46aazsyw9">
              <label htmlFor="filter-credential" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2490" data-mp="ow8vu8ga2fcz">
                Credential
              </label>
              <select id="filter-credential" value={credential} onChange={e => setCredential(e.target.value)} className={selectClass} data-id="element-2491" data-mp="f2iahcfg6l1i">
                <option value={ANY} data-id="element-2492" data-mp="34p7xq70h3loh">All credentials</option>
                {credentials.map(c => <option key={c} value={c} data-id="element-2493" data-mp="1mrl33xuawpni">
                    {c}
                  </option>)}
              </select>
            </div>
            <div data-id="element-2494" data-mp="1hsi6n1sdzrr5">
              <label htmlFor="filter-domain" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2495" data-mp="1hv4buurt1v67">
                Field
              </label>
              <select id="filter-domain" value={domain} onChange={e => setDomain(e.target.value)} className={selectClass} data-id="element-2496" data-mp="1rp3b9m0hv0ug">
                <option value={ANY} data-id="element-2497" data-mp="z0flbd4svnak">All fields</option>
                {programDomains.map(d => <option key={d.slug} value={d.slug} data-id="element-2498" data-mp="182ym7rp0zpu6">
                    {d.name}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500" data-id="element-2499" data-mp="kb9m3p5ypx2o">
            <span aria-live="polite" data-id="element-2500" data-mp="gb98g6qotpwo">
              {results.length} {results.length === 1 ? 'program' : 'programs'}
            </span>
            {isFiltered && <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-navy font-medium hover:underline" data-id="element-2501" data-mp="2keu0neu63bds">
                <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2502" data-mp="2u4dlur5crhbt" />
                Clear filters
              </button>}
            <Link to={`/program-information/${programsIndexSlug}`} className="text-navy font-medium hover:underline" data-id="element-2503" data-mp="1wjhjvdqrcqoz">
              Browse by field
            </Link>
          </div>
        </div>

        {/* Results */}
        {results.length === 0 ? <div className="border border-gray-200 rounded-xl py-16 px-6 text-center" data-id="element-2504" data-mp="2bg8mrwzn4kjc">
            <h2 className="text-lg font-bold text-navy mb-2" data-id="element-2505" data-mp="3hu809d5bldy6">
              No programs match that search
            </h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto" data-id="element-2506" data-mp="3mw72ukt8k6u2">
              Try a broader term, or tell us your goals and we'll suggest
              programs that fit.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-2507" data-mp="168fr8hszil5e">
              <button type="button" onClick={clearFilters} className="px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-2508" data-mp="11fcygbw17fq9">
                Show all programs
              </button>
              <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200" data-id="element-2509" data-mp="nfv65mjj1kc5">
                Get Matched Now
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2510" data-mp="2curvffo8q7ld" />
              </Link>
            </div>
          </div> : <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2511" data-mp="27v6vb1xc3t72">
            {results.map(program => <li key={program.slug} data-id="element-2512" data-mp="1lqh3ppbew7a9">
                <Link to={`/program-information/${program.slug}`} className="group flex flex-col h-full border border-gray-200 rounded-xl p-6 hover:border-navy hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-2513" data-mp="1lpfiis0om4et">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-crimson mb-2" data-id="element-2514" data-mp="y3iqd0il9fgk">
                    {program.credential}
                  </span>
                  <h2 className="text-lg font-bold text-navy leading-snug mb-2 group-hover:underline" data-id="element-2515" data-mp="o4jfrce9j9qx">
                    {program.name}
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-5" data-id="element-2516" data-mp="2ighqjis80dox">
                    {program.heroSubhead}
                  </p>
                  <dl className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-500 mb-5" data-id="element-2517" data-mp="2nfb1b3g4hqle">
                    <div className="flex items-center gap-1.5" data-id="element-2518" data-mp="bt0l4gfl6z5x">
                      <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2519" data-mp="6uflai48yhi0" />
                      <dt className="sr-only" data-id="element-2520" data-mp="liaf61tzqrwo">Length</dt>
                      <dd data-id="element-2521" data-mp="ljv7rlwqw0rw">{program.weeks} weeks</dd>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-2522" data-mp="gr23t093owgv">
                      <BookOpenIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2523" data-mp="uipbrxcentfh" />
                      <dt className="sr-only" data-id="element-2524" data-mp="qhdjfk25cu3l">Credits</dt>
                      <dd data-id="element-2525" data-mp="14hob2pba6cpt">{program.credits} credits</dd>
                    </div>
                  </dl>
                  <span className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between gap-3" data-id="element-2526" data-mp="zi1ymi3a10i2">
                    <span className="text-xs text-gray-500" data-id="element-2527" data-mp="369kjvr62d8gw">
                      {domainName(program.domainSlug)}
                    </span>
                    <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-navy" aria-hidden="true" data-id="element-2528" data-mp="31c18cz0r39sn" />
                  </span>
                </Link>
              </li>)}
          </ul>}
      </div>

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-2529" data-mp="18wy6egdd6u0d">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-id="element-2530" data-mp="25lnjlte1qyg0">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-2531" data-mp="20mazvhih27hn">
            Not sure which program fits?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7" data-id="element-2532" data-mp="1vu9f1778n8g0">
            Answer a few questions about your goals and schedule and we'll
            connect you with accredited schools offering these programs. No
            cost, no obligation.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-sm transition-colors duration-200" data-id="element-2533" data-mp="2jkx0ywdznylk">
            Get Matched Now
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2534" data-mp="b1vsmojlh90a" />
          </Link>
        </div>
      </section>

      <LegalFooter data-id="element-2535" data-mp="f1mfih6034zj" />
    </main>;
}